import { supabase } from './supabase';

export interface Emote {
  id: string;
  slug: string;
  name: string;
  image_url: string | null;
  is_reaction: boolean;
  sort_order: number;
}

const SHORTCODE_RE = /:([a-z0-9_-]+):/gi;

/** Metin içindeki tüm `:slug:` kısayollarını çıkarır (benzersiz, sırasız). */
export function extractShortcodes(text: string): string[] {
  const found = new Set<string>();
  for (const m of text.matchAll(SHORTCODE_RE)) found.add(m[1].toLowerCase());
  return Array.from(found);
}

/**
 * Bir metni düz metin / emote parçalarına böler — XSS'e karşı güvenli:
 * ham HTML asla üretilmez, tüketici (React) parçaları kendi render eder.
 */
export type TextPart = { type: 'text'; value: string } | { type: 'emote'; slug: string; emote?: Emote };

export function parseShortcodes(text: string, emoteBySlug: Map<string, Emote>): TextPart[] {
  const parts: TextPart[] = [];
  let lastIndex = 0;
  for (const m of text.matchAll(SHORTCODE_RE)) {
    const slug = m[1].toLowerCase();
    const emote = emoteBySlug.get(slug);
    // Görseli olmayan/tanımsız kısayolu düz metin olarak bırak.
    if (!emote || !emote.image_url) continue;
    const index = m.index ?? 0;
    if (index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, index) });
    parts.push({ type: 'emote', slug, emote });
    lastIndex = index + m[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });
  return parts;
}

let cache: { data: Emote[]; ts: number } | null = null;
const CACHE_MS = 60_000;

/** Tüm emote setini getirir (public, anon-okur), kısa süre önbellekli. */
export async function fetchEmotes(force = false): Promise<Emote[]> {
  if (!force && cache && Date.now() - cache.ts < CACHE_MS) return cache.data;
  const { data, error } = await supabase
    .from('emotes')
    .select('id, slug, name, image_url, is_reaction, sort_order')
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('Emote seti çekilemedi:', error);
    return cache?.data ?? [];
  }
  cache = { data: (data as Emote[]) ?? [], ts: Date.now() };
  return cache.data;
}

/** Görseli olan, görünür emote'ları sort_order'a göre döner. */
export function usableEmotes(emotes: Emote[]): Emote[] {
  return emotes.filter(e => !!e.image_url);
}

/** Tepki olarak işaretli VE görseli olan emote'lar (en fazla 5). */
export function reactionEmotes(emotes: Emote[]): Emote[] {
  return emotes.filter(e => e.is_reaction && !!e.image_url);
}
